import {h, Fragment} from "preact"
import { useEffect, useMemo, useRef, useState } from "preact/hooks"

import { handleize } from "../../helpers/productVariants"

const IMAGE_DATA_WIDTHS = "[180,240,320,360,420,480,540,650,750,850,950,1080,1200,1296,1400,1512,1600,1728,1800,1944,2000,2160,2376,2592,2808,3024,3200]"
const DESKTOP_BREAKPOINT_QUERY = "(min-width: 1024px)"

const ensureImageSize = (url = "", sizeToken = "") => {
  if (!url || !sizeToken) return url

  try {
    const [withoutQuery, ...queryParts] = url.split("?")
    const query = queryParts.length > 0 ? queryParts.join("?") : ""

    if (!withoutQuery.includes("cdn.shopify.com")) {
      return query ? `${withoutQuery}?${query}` : withoutQuery
    }

    const lastSlashIndex = withoutQuery.lastIndexOf("/")
    if (lastSlashIndex === -1) return url

    const path = withoutQuery.substring(0, lastSlashIndex + 1)
    const filename = withoutQuery.substring(lastSlashIndex + 1)
    const dotIndex = filename.lastIndexOf(".")
    if (dotIndex === -1) return url

    const nameWithoutExtension = filename.substring(0, dotIndex)
    const extension = filename.substring(dotIndex)

    if (nameWithoutExtension.endsWith(sizeToken)) {
      return query ? `${withoutQuery}?${query}` : withoutQuery
    }

    const cleanedName = nameWithoutExtension.replace(/_(\d+x\d+|\d+x)$/i, "")
    const sizedFilename = `${cleanedName}${sizeToken}${extension}`
    const rebuilt = `${path}${sizedFilename}`

    return query ? `${rebuilt}?${query}` : rebuilt
  } catch (error) {
    console.warn("[ProductMedia] Failed to ensure image size", { url, sizeToken, error })
    return url
  }
}

const uniqueUrls = (values = []) => {
  return Array.from(new Set(values.filter(Boolean)))
}

const getColorFromSku = (sku) => {
  return sku?.customFields?.find((field) => field.key === "color")?.value || ""
}

const extractFilename = (url = "") => {
  if (!url) return ""

  const withoutQuery = url.split("?")[0]
  const segments = withoutQuery.split("/")
  return segments[segments.length - 1] || ""
}

const handleizeFilename = (url = "") => {
  const filename = extractFilename(url)
  const withoutExtension = filename.split(".")[0] || filename

  return handleize(withoutExtension)
}

const matchesColorSlug = (value = "", slug = "") => {
  if (!value || !slug) return false

  const normalized = handleize(value)
  if (!normalized) return false

  if (normalized.includes(slug)) return true

  const condensedNormalized = normalized.replace(/-/g, "")
  const condensedSlug = slug.replace(/-/g, "")

  return condensedNormalized.includes(condensedSlug)
}

const matchesColorSlugFromUrl = (url = "", slug = "") => {
  if (!url || !slug) return false

  if (url.toLowerCase().includes(slug)) return true

  return matchesColorSlug(handleizeFilename(url), slug)
}

export default function ProductMedia({
  product,
  fallbackImage,
  onMouseLeave,
  children,
  badge,
  badgeImage,
  selectedColorName,
  swatches,
  shopifyImages = []
}) {
  const sliderWrapperRef = useRef(null)
  const [currentIndex, setCurrentIndex] = useState(0)
  const [isDesktopViewport, setIsDesktopViewport] = useState(() => {
    if (typeof window === "undefined") return true

    return window.matchMedia(DESKTOP_BREAKPOINT_QUERY).matches
  })
  useEffect(() => {
    if (typeof window === "undefined") return

    const mediaQuery = window.matchMedia(DESKTOP_BREAKPOINT_QUERY)

    const handleChange = (event) => {
      setIsDesktopViewport(event.matches)
    }

    if (typeof mediaQuery.addEventListener === "function") {
      mediaQuery.addEventListener("change", handleChange)
    } else if (typeof mediaQuery.addListener === "function") {
      mediaQuery.addListener(handleChange)
    }

    setIsDesktopViewport(mediaQuery.matches)

    return () => {
      if (typeof mediaQuery.removeEventListener === "function") {
        mediaQuery.removeEventListener("change", handleChange)
      } else if (typeof mediaQuery.removeListener === "function") {
        mediaQuery.removeListener(handleChange)
      }
    }
  }, [])

  const shopifyGalleryUrls = useMemo(() => {
    if (!Array.isArray(shopifyImages)) return []

    return shopifyImages
      .map((image) => image?.url)
      .filter(Boolean)
  }, [shopifyImages])

  const skuImages = useMemo(() => {
    if (!Array.isArray(product?.skus)) return []

    return product.skus
      .map((sku) => sku?.imageUrl)
      .filter(Boolean)
  }, [product?.skus])

  const allImages = useMemo(() => {
    const mergedImages = [
      product?.imageUrl,
      ...(product?.alternateImageUrls || []),
      ...shopifyGalleryUrls,
      ...skuImages,
      fallbackImage
    ]

    return uniqueUrls(mergedImages)
  }, [product?.imageUrl, product?.alternateImageUrls, shopifyGalleryUrls, skuImages, fallbackImage])

  const sizeToken = isDesktopViewport ? "_840x" : "_606x"

  const sizedAllImages = useMemo(() => {
    return uniqueUrls(allImages.map((url) => ensureImageSize(url, sizeToken)))
  }, [allImages, sizeToken])

  const imagesByColor = useMemo(() => {
    if (!Array.isArray(swatches) || swatches.length === 0) return {}
    const map = {}

    swatches.forEach((color) => {
      const slug = handleize(color)
      if (!slug) return

      const skuMatches = (product?.skus || [])
        .filter((sku) => {
          const colorValue = handleize(getColorFromSku(sku))
          if (colorValue === slug) return true
          return matchesColorSlugFromUrl(sku?.imageUrl, slug)
        })
        .map((sku) => ensureImageSize(sku?.imageUrl, sizeToken))
        .filter(Boolean)

      const shopifyMatches = (Array.isArray(shopifyImages) ? shopifyImages : [])
        .filter((image) => {
          const url = image?.url || ""
          const alt = image?.altText || ""

          if (matchesColorSlug(alt, slug)) return true
          return matchesColorSlugFromUrl(url, slug)
        })
        .map((image) => ensureImageSize(image?.url, sizeToken))

      const primaryImage = ensureImageSize(product?.imageUrl, sizeToken)
      const sourceImages = matchesColorSlugFromUrl(primaryImage, slug)
        ? [primaryImage, ...shopifyMatches, ...skuMatches]
        : [...shopifyMatches, ...skuMatches]

      let matchingImages = uniqueUrls(sourceImages)

      if (matchingImages.length < 2) {
        const urlMatches = sizedAllImages.filter((url) => matchesColorSlugFromUrl(url, slug))
        matchingImages = uniqueUrls([...matchingImages, ...urlMatches])
      }

      let combined = matchingImages

      if (combined.length === 0 && sizedAllImages.length > 0) {
        console.warn("[ProductMedia] Falling back to full gallery for color", {
          productName: product?.name,
          color,
          slug,
          imageCount: sizedAllImages.length
        })
        combined = [...sizedAllImages]
      }

      if (combined.length) {
        map[slug] = combined
      }
    })

    return map
  }, [swatches, product?.skus, shopifyImages, sizedAllImages, sizeToken])

  const activeImages = useMemo(() => {
    const slug = handleize(selectedColorName)

    if (slug && imagesByColor[slug]?.length) {
      return imagesByColor[slug]
    }

    return sizedAllImages
  }, [selectedColorName, imagesByColor, sizedAllImages])

  useEffect(() => {
    if (!sliderWrapperRef.current) return

    sliderWrapperRef.current.scrollTo({ left: 0 })
    setCurrentIndex(0)
  }, [activeImages, isDesktopViewport])

  const scrollToIndex = (index) => {
    if (sliderCount === 0) return

    const wrapper = sliderWrapperRef.current
    if (!wrapper) return

    const clampedIndex = Math.max(0, Math.min(index, sliderCount - 1))

    const scrollAmount = wrapper.clientWidth * clampedIndex

    if (typeof wrapper.scrollTo === "function") {
      wrapper.scrollTo({ left: scrollAmount, behavior: "smooth" })
    } else {
      wrapper.scrollLeft = scrollAmount
    }

    setCurrentIndex(clampedIndex)
  }

  const handlePrev = (event) => {
    if (sliderCount === 0) return
    event.preventDefault()
    event.stopPropagation()
    scrollToIndex(currentIndex - 1)
  }

  const handleNext = (event) => {
    if (sliderCount === 0) return
    event.preventDefault()
    event.stopPropagation()
    scrollToIndex(currentIndex + 1)
  }

  const handleKeyActivate = (event, callback, disabled) => {
    if (disabled) return
    if (event.key === "Enter" || event.key === " ") {
      event.preventDefault()
      event.stopPropagation()
      callback(event)
    }
  }

  const handleScroll = () => {
    if (sliderCount === 0) return

    const wrapper = sliderWrapperRef.current
    if (!wrapper) return

    const { scrollLeft, clientWidth } = wrapper
    const index = clientWidth ? Math.round(scrollLeft / clientWidth) : 0

    setCurrentIndex(Math.max(0, Math.min(index, sliderCount - 1)))
  }

  const sliderImages = useMemo(() => {
    if (!Array.isArray(activeImages) || activeImages.length <= 1) return []

    return isDesktopViewport ? activeImages.slice(1) : activeImages
  }, [activeImages, isDesktopViewport])

  const sliderCount = sliderImages.length
  const totalImages = activeImages.length
  const hasSlider = totalImages > 1
  const featuredImage = activeImages[0] ?? ensureImageSize(fallbackImage, sizeToken)
  const sliderLabelOffset = isDesktopViewport ? 2 : 1
  const showNavigation = isDesktopViewport && sliderCount > 0
  const badgeText = typeof badge?.text === "string" ? badge.text.trim() : ""
  const badgeClasses = ["plp-item__badge", "badge"]
  if (badge?.className) badgeClasses.push(badge.className)
  const hasBadgeImage = Boolean(badgeImage?.src)
  const badgeImageAlt = typeof badgeImage?.alt === "string" ? badgeImage.alt : ""

  useEffect(() => {
    if (sliderCount === 0) {
      if (currentIndex !== 0) setCurrentIndex(0)
      return
    }

    if (currentIndex > sliderCount - 1) {
      setCurrentIndex(sliderCount - 1)
    }
  }, [sliderCount, currentIndex])

  const handleMouseLeave = (event) => {
    if (typeof onMouseLeave === "function") {
      onMouseLeave(event)
    }

    if (typeof document !== "undefined" && document.activeElement) {
      document.activeElement.blur()
    }
  }

  return (
    <div
      class="ns-product-image-container ns-relative"
      onMouseLeave={handleMouseLeave}
    >
      <div class="plp-item__featured">
        {hasSlider && (
          <div class="plp-item__slider">
            <div
              ref={sliderWrapperRef}
              class="plp-item__slider-wrapper"
              role="listbox"
              aria-label="Product image gallery"
              onScroll={handleScroll}
            >
              {sliderImages.map((imageUrl, index) => (
                <div
                  key={`${imageUrl}-${index}`}
                  class="plp-item__slider-slide"
                  role="option"
                  aria-selected={currentIndex === index}
                >
                  <div class="img fit-cover plp-item__image">
                    <img
                      class="img__el lazyload slider-image"
                      data-src={imageUrl}
                      data-widths={IMAGE_DATA_WIDTHS}
                      data-sizes="auto"
                      alt={product?.name || "Product image"}
                      aria-label={`${product?.name || "Product"} image ${index + sliderLabelOffset} of ${totalImages}`}
                      loading="lazy"
                    />
                  </div>
                </div>
              ))}
            </div>

            {showNavigation && (
              <>
                <span
                  role="button"
                  tabIndex={currentIndex === 0 ? -1 : 0}
                  class="plp-item__slider-nav plp-item__slider-nav--prev"
                  aria-label="Previous"
                  aria-disabled={currentIndex === 0}
                  onClick={(event) => {
                    event.stopPropagation()
                    event.preventDefault()
                    if (currentIndex === 0) return
                    handlePrev(event)
                  }}
                  onKeyDown={(event) => handleKeyActivate(event, handlePrev, currentIndex === 0)}
                >
                  <svg viewBox="0 0 100 100" aria-hidden="true" focusable="false">
                    <path d="M 10,50 L 60,100 L 70,90 L 30,50  L 70,10 L 60,0 Z" />
                  </svg>
                </span>

                <span
                  role="button"
                  tabIndex={currentIndex === sliderCount - 1 ? -1 : 0}
                  class="plp-item__slider-nav plp-item__slider-nav--next"
                  aria-label="Next"
                  aria-disabled={currentIndex === sliderCount - 1}
                  onClick={(event) => {
                    event.stopPropagation()
                    event.preventDefault()
                    if (currentIndex === sliderCount - 1) return
                    handleNext(event)
                  }}
                  onKeyDown={(event) => handleKeyActivate(event, handleNext, currentIndex === sliderCount - 1)}
                >
                  <svg viewBox="0 0 100 100" aria-hidden="true" focusable="false">
                    <path d="M 10,50 L 60,100 L 70,90 L 30,50  L 70,10 L 60,0 Z" transform="translate(100, 100) rotate(180)" />
                  </svg>
                </span>
              </>
            )}
          </div>
        )}

        <div class="img fit-cover plp-item__image plp-item__image--featured">
          <img
            class="img__el lazyload"
            data-src={featuredImage}
            data-widths={IMAGE_DATA_WIDTHS}
            data-sizes="auto"
            alt={product?.name || "Product image"}
            aria-label={`${product?.name || "Product"} featured image`}
            loading="lazy"
          />
          <div class="img__loader"></div>
        </div>

        {children}

        {badgeText && (
          <span class={badgeClasses.join(" ")}>
            {badgeText}
          </span>
        )}

        {hasBadgeImage && (
          <div class={`plp-item__badge-image${badgeText ? " plp-item__badge-image--spaced" : ""}`}>
            <img
              src={badgeImage.src}
              alt={badgeImageAlt}
              class="plp-item__badge-image-logo"
              height="60"
              width="60"
              loading="lazy"
            />
          </div>
        )}
      </div>
    </div>
  )
}
